import { createContext, useContext, useEffect, useState } from "react";
import {
  getShellDesign,
  getNozzlesDesign,
  getAllData,
  postAllData,
  postShellDesign,
  postNozzlesDesign,
} from "../api";
import { useParams } from "react-router-dom";
import { useFormContext } from "react-hook-form";
import { useGeneralContext } from "../../../AllEquipments/CommonFeatures/SidePanel/GeneralContext";
import {
  computeShellUID,
  computeNozzleUID,
} from "../../../../../utils/pvUID";

const PVContext = createContext();

const DESIGN_FIELD_BINDINGS = {
  TextBox11: (payload) => payload?.dataVessel?.PnPID,
  TextBox92: (payload) => payload?.dataVessel?.Designextpressure,
  ComboBox7: (payload) => payload?.dataVessel?.DiameterBasis,
  ComboBox3: (payload) => payload?.dataVessel?.HeadType,
  ComboBox1: (payload) => payload?.dataVessel?.SupportType,
  TextBox66: (payload) => payload?.dataVessel?.LiquidHeight,
  TextBox30: (payload) => payload?.dataVessel?.LiquidDensity,
  TextBox29: (payload) => payload?.dataVessel?.InsulationDensity,
  ComboBox6: (payload) => payload?.dataVessel?.ImpactTest,
  ComboBox8: (payload) => payload?.dataVessel?.HydrotestDesign,
  TextBox67: (payload) => payload?.dataVessel?.HydrotestPressure,
  TextBox32: (payload) => payload?.dataVessel?.JointEfficiencyCirc,
  TextBox31: (payload) => payload?.dataVessel?.JointEfficiencyLong,
  TextBox94: (payload) => payload?.dataVessel?.MDMT,
  ComboBox5: (payload) => payload?.dataVessel?.Orientation,
  TextBox_Seismic_Spec: (payload) => payload?.dataVessel?.SeismicDesign,
  TextBox49: (payload) => payload?.dataVessel?.WindSpeed,
  TextBox50: (payload) => payload?.dataVessel?.WindDesignCode,
  ComboBox20: (payload) => payload?.dataVessel?.Radiography,
  ComboBox18: (payload) => payload?.dataVessel?.PWHT,
  TextBox48: (payload) => payload?.dataEquip?.Tag,
  txtVIDia: (payload) => payload?.dataTank?.Diameter,
  txtVLenTT: (payload) => payload?.dataTank?.Height,
  txt_SF: (payload) => payload?.dataTank?.Width,
  TextBox80: (payload) => payload?.dataTank?.Insulation,
  txtDPressure: (payload) => payload?.dataTank?.DesignPressure,
  txtDTemp: (payload) => payload?.dataTank?.DesignTemperature,
  txtVCA: (payload) => payload?.dataVessel?.CorrosionAllowance,
  TextBox28: (payload) => payload?.dataVessel?.CorrosionAllowanceNozzle,
  TextBox52: (payload) => payload?.dataVessel?.FlangeRating,
  TextBox64: (payload) => payload?.dataVessel?.ASMEB165Group,
  TextBox2: (payload) => payload?.dataVessel?.ProjectNo,
  TextBox10: (payload) => payload?.dataVessel?.RevNo,
  TextBox1: (payload) => payload?.dataVessel?.Client,
  TextBox89: (payload) => payload?.dataVessel?.CRN,
  ComboBox24: (payload) => payload?.dataVessel?.DesignCode,
  TextBox91: (payload) => payload?.dataVessel?.EditionAddenda,
  ComboBox2: (payload) => payload?.dataVessel?.UStamp,
  SI_Units_RB: (payload) => payload?.dataVessel?.SIUnits,
  Eng_Units_RB: (payload) => payload?.dataVessel?.EnglishUnits,
  ComboBox9: (payload) => payload?.dataVessel?.ShellMaterial,
  ComboBox11: (payload) => payload?.dataVessel?.HeadMaterial,
  ComboBox12: (payload) => payload?.dataVessel?.NozzleMaterial,
  ComboBox13: (payload) => payload?.dataVessel?.FlangeForging,
  ComboBox14: (payload) => payload?.dataVessel?.RFPadMaterial,
  ComboBox15: (payload) => payload?.dataVessel?.StudBoltNutMaterial,
  ComboBox16: (payload) => payload?.dataVessel?.GasketMaterial,
  ComboBox_ShellReq: (payload) => payload?.dataVessel?.ShellMaterialReq,
  ComboBox_HeadReq: (payload) => payload?.dataVessel?.HeadMaterialReq,
  ComboBox_NozzleReq: (payload) => payload?.dataVessel?.NozzleMaterialReq,
  ComboBox_FlangeReq: (payload) => payload?.dataVessel?.FlangeForgingReq,
  ComboBox_PadReq: (payload) => payload?.dataVessel?.RFPadMaterialReq,
  ComboBox_StudReq: (payload) => payload?.dataVessel?.StudBoltNutMaterialReq,
  ComboBox_GaskReq: (payload) => payload?.dataVessel?.GasketMaterialReq,
  TextBox68: (payload) => payload?.dataVessel?.ShellStress,
  TextBox88: (payload) => payload?.dataVessel?.ShellCurve,
  TextBox73: (payload) => payload?.dataVessel?.HeadStress,
  TextBox90: (payload) => payload?.dataVessel?.HeadCurve,
  TextBox78: (payload) => payload?.dataVessel?.NozzleStress,
  TextBox95: (payload) => payload?.dataVessel?.NozzleCurve,
  TextBox79: (payload) => payload?.dataVessel?.FlangeStress,
  TextBox96: (payload) => payload?.dataVessel?.FlangeCurve,
  TextBox81: (payload) => payload?.dataVessel?.RFPadStress,
  TextBox102: (payload) => payload?.dataVessel?.RFPadCurve,
  TextBox82: (payload) => payload?.dataVessel?.StudBoltNutStress,
  TextBox103: (payload) => payload?.dataVessel?.StudBoltNutCurve,
  TextBox86: (payload) => payload?.dataVessel?.GasketStress,
  TextBox104: (payload) => payload?.dataVessel?.GasketCurve,
};

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const normalizeRevisionKey = (value) => {
  const nextValue = String(value ?? "").trim();
  return nextValue || "0";
};

const cloneData = (value) => {
  try {
    return structuredClone(value);
  } catch {
    return JSON.parse(JSON.stringify(value ?? {}));
  }
};

const getRevisionBucket = (source) => {
  if (!isPlainObject(source)) return {};
  if (isPlainObject(source.revisions)) return source.revisions;
  if (isPlainObject(source.revisionsData)) return source.revisionsData;
  if (isPlainObject(source.inputDataByRevision)) return source.inputDataByRevision;
  if (isPlainObject(source.shellDesignByRevision)) return source.shellDesignByRevision;
  if (isPlainObject(source.nozzlesDesignByRevision)) return source.nozzlesDesignByRevision;
  if (isPlainObject(source.inputData?.revisions)) return source.inputData.revisions;
  return {};
};

const getRevisionValue = (source, revisionKey) => {
  const bucket = getRevisionBucket(source);
  return bucket?.[revisionKey];
};

const normalizeShellRows = (shellSource) => {
  if (Array.isArray(shellSource?.shellDesign)) return shellSource.shellDesign;
  if (Array.isArray(shellSource)) return shellSource;
  return [];
};

const normalizeNozzleRows = (nozzleSource) => {
  const source = isPlainObject(nozzleSource) ? nozzleSource : {};

  return {
    DataGridView1: Array.isArray(source.DataGridView1)
      ? source.DataGridView1
      : Array.isArray(source.nozzlesDesign)
        ? source.nozzlesDesign
        : [],
    DataGridView10: Array.isArray(source.DataGridView10)
      ? source.DataGridView10
      : Array.isArray(source.nozzlesDesign)
        ? source.nozzlesDesign
        : [],
  };
};

const summarizeShellRows = (rows) => {
  const firstRow = Array.isArray(rows) && rows.length > 0 ? rows[0] : null;

  return {
    count: Array.isArray(rows) ? rows.length : 0,
    firstComponent: firstRow?.Component,
    firstLongSeam: firstRow?.["Long Seam"],
    firstProvidedThk: firstRow?.["Provided Thk"],
    firstCanLength: firstRow?.["Can Length"],
  };
};

const summarizeShellRevisionMap = (revisionMap) => {
  const source = isPlainObject(revisionMap) ? revisionMap : {};

  return Object.fromEntries(
    Object.entries(source).map(([revisionKey, payload]) => [
      revisionKey,
      summarizeShellRows(
        normalizeShellRows(payload?.shell || payload?.inputData?.shell || payload),
      ),
    ]),
  );
};

const restoreDesignFields = (setValue, payload) => {
  Object.entries(DESIGN_FIELD_BINDINGS).forEach(([fieldName, getter]) => {
    setValue(fieldName, getter(payload));
  });
};

const assignDesignFields = (target, payload) => {
  Object.entries(DESIGN_FIELD_BINDINGS).forEach(([fieldName, getter]) => {
    target[fieldName] = getter(payload);
  });
};

export const usePVContext = () => {
  const context = useContext(PVContext);

  if (!context) {
    throw new Error("usePVContext must be used within a PVProvider");
  }

  return context;
};

function PVProvider({ children }) {
  const [revisionLoadVersion, setRevisionLoadVersion] = useState(0);

  const { id: projectId } = useParams();
  const [selectedProjectId, setSelectedProjectId] = useState(projectId ?? null);
  const activeProjectId = selectedProjectId ?? projectId;

  const { control, setValue, getValues, reset } = useFormContext();
  const { selectedModelingRevision, revision } = useGeneralContext();

  const currentRevision = normalizeRevisionKey(
    selectedModelingRevision ?? revision?.name ?? revision,
  );

  const extractDesignPayload = (formData) => {
    const dataVessel = {};
    const dataTank = {};
    const dataEquip = {};

    dataVessel.PnPID = formData?.TextBox11;
    dataVessel.Designextpressure = formData?.TextBox92;
    dataVessel.DiameterBasis = formData?.ComboBox7;
    dataVessel.HeadType = formData?.ComboBox3;
    dataVessel.SupportType = formData?.ComboBox1;
    dataVessel.LiquidHeight = formData?.TextBox66;
    dataVessel.LiquidDensity = formData?.TextBox30;
    dataVessel.InsulationDensity = formData?.TextBox29;
    dataVessel.ImpactTest = formData?.ComboBox6;
    dataVessel.HydrotestDesign = formData?.ComboBox8;
    dataVessel.HydrotestPressure = formData?.TextBox67;
    dataVessel.JointEfficiencyCirc = formData?.TextBox32;
    dataVessel.JointEfficiencyLong = formData?.TextBox31;
    dataVessel.MDMT = formData?.TextBox94;
    dataVessel.Orientation = formData?.ComboBox5;
    dataVessel.SeismicDesign = formData?.TextBox_Seismic_Spec;
    dataVessel.WindSpeed = formData?.TextBox49;
    dataVessel.WindDesignCode = formData?.TextBox50;
    dataVessel.Radiography = formData?.ComboBox20;
    dataVessel.PWHT = formData?.ComboBox18;
    dataVessel.CorrosionAllowance = formData?.txtVCA;
    dataVessel.CorrosionAllowanceNozzle = formData?.TextBox28;
    dataVessel.FlangeRating = formData?.TextBox52;
    dataVessel.ASMEB165Group = formData?.TextBox64;
    dataVessel.ProjectNo = formData?.TextBox2;
    dataVessel.RevNo = formData?.TextBox10;
    dataVessel.Client = formData?.TextBox1;
    dataVessel.CRN = formData?.TextBox89;
    dataVessel.DesignCode = formData?.ComboBox24;
    dataVessel.EditionAddenda = formData?.TextBox91;
    dataVessel.UStamp = formData?.ComboBox2;
    dataVessel.SIUnits = formData?.SI_Units_RB;
    dataVessel.EnglishUnits = formData?.Eng_Units_RB;
    dataVessel.ShellMaterial = formData?.ComboBox9;
    dataVessel.HeadMaterial = formData?.ComboBox11;
    dataVessel.NozzleMaterial = formData?.ComboBox12;
    dataVessel.FlangeForging = formData?.ComboBox13;
    dataVessel.RFPadMaterial = formData?.ComboBox14;
    dataVessel.StudBoltNutMaterial = formData?.ComboBox15;
    dataVessel.GasketMaterial = formData?.ComboBox16;
    dataVessel.ShellMaterialReq = formData?.ComboBox_ShellReq;
    dataVessel.HeadMaterialReq = formData?.ComboBox_HeadReq;
    dataVessel.NozzleMaterialReq = formData?.ComboBox_NozzleReq;
    dataVessel.FlangeForgingReq = formData?.ComboBox_FlangeReq;
    dataVessel.RFPadMaterialReq = formData?.ComboBox_PadReq;
    dataVessel.StudBoltNutMaterialReq = formData?.ComboBox_StudReq;
    dataVessel.GasketMaterialReq = formData?.ComboBox_GaskReq;
    dataVessel.ShellStress = formData?.TextBox68;
    dataVessel.ShellCurve = formData?.TextBox88;
    dataVessel.HeadStress = formData?.TextBox73;
    dataVessel.HeadCurve = formData?.TextBox90;
    dataVessel.NozzleStress = formData?.TextBox78;
    dataVessel.NozzleCurve = formData?.TextBox95;
    dataVessel.FlangeStress = formData?.TextBox79;
    dataVessel.FlangeCurve = formData?.TextBox96;
    dataVessel.RFPadStress = formData?.TextBox81;
    dataVessel.RFPadCurve = formData?.TextBox102;
    dataVessel.StudBoltNutStress = formData?.TextBox82;
    dataVessel.StudBoltNutCurve = formData?.TextBox103;
    dataVessel.GasketStress = formData?.TextBox86;
    dataVessel.GasketCurve = formData?.TextBox104;

    dataEquip.Tag = formData?.TextBox48;

    dataTank.Diameter = formData?.txtVIDia;
    dataTank.Height = formData?.txtVLenTT;
    dataTank.Width = formData?.txt_SF;
    dataTank.Insulation = formData?.TextBox80;
    dataTank.DesignPressure = formData?.txtDPressure;
    dataTank.DesignTemperature = formData?.txtDTemp;

    return { dataVessel, dataTank, dataEquip };
  };

  const extractNozzleRowsWithGeometry = (formData) => {
    const nozzleRows = Array.isArray(formData?.DataGridView1)
      ? formData.DataGridView1
      : [];

    const geometryRows = Array.isArray(formData?.DataGridView10)
      ? formData.DataGridView10
      : [];

    return nozzleRows.map((row, i) => ({
      ...row,
      ...(geometryRows[i] || {}),
      rowNo: i,
    }));
  };

  async function savePVData() {
    if (!activeProjectId) {
      throw new Error("Cannot save PV data without selectedProjectId/projectId");
    }

    await new Promise((resolve) => setTimeout(resolve, 0));

    const formSnapshot = cloneData(getValues());
    const { dataVessel, dataTank, dataEquip } = extractDesignPayload(formSnapshot);

    const shellRows = Array.isArray(formSnapshot?.DataGridView5)
      ? formSnapshot.DataGridView5
      : [];

    const nozzleRowsRaw = Array.isArray(formSnapshot?.DataGridView1)
      ? formSnapshot.DataGridView1
      : [];

    const geometryRowsRaw = Array.isArray(formSnapshot?.DataGridView10)
      ? formSnapshot.DataGridView10
      : [];

    const nozzleRows = extractNozzleRowsWithGeometry(formSnapshot);

    const estimationData = {
      DataGridView2: Array.isArray(formSnapshot?.DataGridView2)
        ? formSnapshot.DataGridView2
        : [],
      DataGridView7: Array.isArray(formSnapshot?.DataGridView7)
        ? formSnapshot.DataGridView7
        : [],
      DataGridView8: Array.isArray(formSnapshot?.DataGridView8)
        ? formSnapshot.DataGridView8
        : [],
      totalMaterialCost: formSnapshot?.totalMaterialCost,
      totalHours: formSnapshot?.totalHours,
      totalFabCost: formSnapshot?.totalFabCost,
      totalCost: formSnapshot?.totalCost,
      TextBox41: formSnapshot?.TextBox41,
      TextBox35: formSnapshot?.TextBox35,
      bomData: Array.isArray(formSnapshot?.bomData)
        ? formSnapshot.bomData
        : [],
    };

    console.log("[PVProvider] saving estimation data:", {
      dgv2Len: estimationData.DataGridView2.length,
      dgv7Len: estimationData.DataGridView7.length,
      dgv8Len: estimationData.DataGridView8.length,
      totalMaterialCost: estimationData.totalMaterialCost,
      totalHours: estimationData.totalHours,
    });

    const calcKeys = Object.keys(formSnapshot).filter(
      (key) =>
        key.startsWith("txt_") ||
        key.startsWith("text_") ||
        key.startsWith("Label") ||
        key.startsWith("label"),
    );

    const calculationData = {};

    for (const key of calcKeys) {
      calculationData[key] = formSnapshot[key];
    }

    const revisionPayload = {
      dataVessel,
      dataTank,
      dataEquip,
      inputData: {
        design: { dataVessel, dataTank, dataEquip },
        shell: { shellDesign: shellRows },
        nozzle: {
          DataGridView1: nozzleRowsRaw,
          DataGridView10: geometryRowsRaw,
          nozzlesDesign: nozzleRows,
        },
        estimation: estimationData,
        calculation: calculationData,
      },
    };

    console.log("[PVProvider] save requested", {
      projectId: activeProjectId,
      selectedProjectId,
      currentRevision,
      shellRows: shellRows.length,
      nozzleRows: nozzleRows.length,
      rawNozzleRows: nozzleRowsRaw.length,
      geometryRows: geometryRowsRaw.length,
    });

    const [existingAllRes, existingShellRes, existingNozzleRes] =
      await Promise.all([
        getAllData(activeProjectId).catch(() => null),
        getShellDesign(activeProjectId).catch(() => null),
        getNozzlesDesign(activeProjectId).catch(() => null),
      ]);

    const existingAllData = existingAllRes?.all || {};

    const existingShellData = isPlainObject(
      existingShellRes?.data?.shellDesign?.data,
    )
      ? existingShellRes.data.shellDesign.data
      : {};

    const existingNozzleData = isPlainObject(
      existingNozzleRes?.data?.nozzlesDesign?.data,
    )
      ? existingNozzleRes.data.nozzlesDesign.data
      : {};

    const shellRowsWithRowNo = shellRows.map((row, i) => ({
      ...row,
      rowNo: i,
    }));

    const mergedAllRevisions = {
      ...getRevisionBucket(existingAllData),
      [currentRevision]: cloneData(revisionPayload),
    };

    const mergedShellRevisions = {
      ...getRevisionBucket(existingShellData),
      [currentRevision]: {
        shellDesign: cloneData(shellRowsWithRowNo),
      },
    };

    const mergedNozzleRevisions = {
      ...getRevisionBucket(existingNozzleData),
      [currentRevision]: {
        DataGridView1: cloneData(nozzleRowsRaw),
        DataGridView10: cloneData(geometryRowsRaw),
        nozzlesDesign: cloneData(nozzleRows),
      },
    };

    const mergedInputDataRevisions = Object.fromEntries(
      Object.entries(mergedAllRevisions).map(([revisionKey, payload]) => [
        revisionKey,
        cloneData(payload?.inputData || {}),
      ]),
    );

    console.log("[PVProvider] save revision buckets", {
      currentRevision,
      allRevisionKeys: Object.keys(mergedAllRevisions),
      shellRevisionKeys: Object.keys(mergedShellRevisions),
      nozzleRevisionKeys: Object.keys(mergedNozzleRevisions),
      savedShellSignature: summarizeShellRows(shellRowsWithRowNo),
    });

    console.log(
      "[PVProvider] save revision bucket details",
      JSON.stringify(
        {
          currentRevision,
          allDataShellByRevision: summarizeShellRevisionMap(mergedAllRevisions),
          shellEndpointByRevision: summarizeShellRevisionMap(mergedShellRevisions),
        },
        null,
        2,
      ),
    );

    const saveRequests = [
      postAllData(activeProjectId, {
        ...existingAllData,
        ...revisionPayload,
        revision: currentRevision,
        activeRevision: currentRevision,
        revisions: mergedAllRevisions,
        revisionsData: mergedAllRevisions,
        inputDataByRevision: mergedAllRevisions,
        inputData: {
          ...cloneData(revisionPayload.inputData),
          activeRevision: currentRevision,
          revisions: mergedInputDataRevisions,
        },
      }),
      postShellDesign(activeProjectId, {
        data: {
          ...existingShellData,
          revision: currentRevision,
          activeRevision: currentRevision,
          shellDesign: shellRowsWithRowNo,
          revisions: mergedShellRevisions,
          shellDesignByRevision: mergedShellRevisions,
        },
      }),
      postNozzlesDesign(activeProjectId, {
        data: {
          ...existingNozzleData,
          revision: currentRevision,
          activeRevision: currentRevision,
          DataGridView1: nozzleRowsRaw,
          DataGridView10: geometryRowsRaw,
          nozzlesDesign: nozzleRows,
          revisions: mergedNozzleRevisions,
          nozzlesDesignByRevision: mergedNozzleRevisions,
        },
      }),
    ];

    const results = await Promise.allSettled(saveRequests);
    const failedCount = results.filter((r) => r.status === "rejected").length;

    if (failedCount === results.length) {
      throw new Error("Failed to save PV input data");
    }

    return {
      ok: failedCount === 0,
      failedCount,
    };
  }

  async function loadPvData(projectIdValue) {
    if (!projectIdValue) return;

    console.log("[PVProvider] load requested", {
      projectId: projectIdValue,
      selectedProjectId,
      currentRevision,
      selectedModelingRevision,
    });

    const [resShell, resNozzle, allDataRes] = await Promise.all([
      getShellDesign(projectIdValue),
      getNozzlesDesign(projectIdValue),
      getAllData(projectIdValue).catch(() => null),
    ]);

    const allData = allDataRes?.all || {};
    const allRevisionData = getRevisionValue(allData, currentRevision);

    const nestedInputRevisionData = isPlainObject(allData?.inputData?.revisions)
      ? allData.inputData.revisions?.[currentRevision]
      : null;

    const activeAllData = isPlainObject(allRevisionData)
      ? allRevisionData
      : isPlainObject(nestedInputRevisionData)
        ? {
            ...allData,
            inputData: nestedInputRevisionData,
          }
        : allData;

    const allInputData = activeAllData?.inputData || {};

    const shellPayload = isPlainObject(resShell?.data?.shellDesign?.data)
      ? resShell.data.shellDesign.data
      : {};

    const shellRevisionData = getRevisionValue(shellPayload, currentRevision);
    const allDataShellRows = normalizeShellRows(allInputData?.shell);

    const endpointShellRows = normalizeShellRows(
      shellRevisionData || shellPayload || resShell?.data?.shellDesign,
    );

    const nextDgv5 = normalizeShellRows(
      shellRevisionData ||
        shellPayload ||
        allInputData?.shell ||
        resShell?.data?.shellDesign,
    );

    const nozzlePayload = isPlainObject(resNozzle?.data?.nozzlesDesign?.data)
      ? resNozzle.data.nozzlesDesign.data
      : {};

    const nozzleRevisionData = getRevisionValue(nozzlePayload, currentRevision);

    const nozzleFallback = Array.isArray(resNozzle?.data?.nozzlesDesign)
      ? {
          DataGridView1: resNozzle.data.nozzlesDesign,
          DataGridView10: resNozzle.data.nozzlesDesign,
        }
      : {};

    const nextNozzleData = normalizeNozzleRows(
      nozzleRevisionData || nozzlePayload || allInputData?.nozzle || nozzleFallback,
    );

    console.log("[PVProvider] load resolved", {
      currentRevision,
      hasAllRevisionData: isPlainObject(allRevisionData),
      hasNestedInputRevisionData: isPlainObject(nestedInputRevisionData),
      allRevisionKeys: Object.keys(getRevisionBucket(allData)),
      shellRevisionKeys: Object.keys(getRevisionBucket(shellPayload)),
      nozzleRevisionKeys: Object.keys(getRevisionBucket(nozzlePayload)),
      restoredShellRows: nextDgv5.length,
      restoredNozzleRows: nextNozzleData.DataGridView1.length,
      allDataShellSignature: summarizeShellRows(allDataShellRows),
      endpointShellSignature: summarizeShellRows(endpointShellRows),
      selectedShellSignature: summarizeShellRows(nextDgv5),
    });

    console.log(
      "[PVProvider] load revision bucket details",
      JSON.stringify(
        {
          currentRevision,
          allDataShellByRevision: summarizeShellRevisionMap(getRevisionBucket(allData)),
          shellEndpointByRevision: summarizeShellRevisionMap(
            getRevisionBucket(shellPayload),
          ),
          selectedShellSignature: summarizeShellRows(nextDgv5),
        },
        null,
        2,
      ),
    );

    const restoredDesignPayload = {
      dataVessel: activeAllData?.dataVessel,
      dataTank: activeAllData?.dataTank,
      dataEquip: activeAllData?.dataEquip,
    };

    const nextFormValues = cloneData(getValues());

    assignDesignFields(nextFormValues, restoredDesignPayload);

    const projectTag = nextFormValues.TextBox48 || "";

    nextDgv5.forEach((row) => {
      if (!row.UID && row.Component) {
        row.UID = computeShellUID(row.Component, projectTag) || "";
      }
    });

    nextNozzleData.DataGridView1.forEach((row) => {
      if (!row.UID && row.Mark) {
        row.UID = computeNozzleUID(row.Mark, projectTag) || "";
      }
    });

    nextFormValues.DataGridView5 = nextDgv5;
    nextFormValues.DataGridView1 = nextNozzleData.DataGridView1;
    nextFormValues.DataGridView10 = nextNozzleData.DataGridView10;

    console.log("[PVProvider] resetting form for revision", {
      currentRevision,
      designRevNo: restoredDesignPayload?.dataVessel?.RevNo,
      projectNo: restoredDesignPayload?.dataVessel?.ProjectNo,
      shellRows: nextDgv5.length,
      nozzleRows: nextNozzleData.DataGridView1.length,
    });

    const allEstimationData = allInputData?.estimation || {};

    console.log("[PVProvider] restoring estimation data", {
      currentRevision,
      dgv2Len: allEstimationData.DataGridView2?.length,
      dgv7Len: allEstimationData.DataGridView7?.length,
      dgv8Len: allEstimationData.DataGridView8?.length,
      totalMaterialCost: allEstimationData.totalMaterialCost,
      totalHours: allEstimationData.totalHours,
    });

    nextFormValues.DataGridView2 = allEstimationData.DataGridView2 || [];
    nextFormValues.DataGridView7 = allEstimationData.DataGridView7 || [];
    nextFormValues.DataGridView8 = allEstimationData.DataGridView8 || [];
    nextFormValues.bomData = allEstimationData.bomData || [];
    nextFormValues.totalMaterialCost = allEstimationData.totalMaterialCost;
    nextFormValues.totalHours = allEstimationData.totalHours;
    nextFormValues.totalFabCost = allEstimationData.totalFabCost;
    nextFormValues.totalCost = allEstimationData.totalCost;
    nextFormValues.TextBox41 = allEstimationData.TextBox41;
    nextFormValues.TextBox35 = allEstimationData.TextBox35;

    const allCalcData = allInputData?.calculation || {};

    for (const [key, value] of Object.entries(allCalcData)) {
      nextFormValues[key] = value;
    }

    reset(nextFormValues);
    setRevisionLoadVersion((version) => version + 1);
  }

  useEffect(() => {
    if (!projectId) return;

    setSelectedProjectId(projectId);
  }, [projectId]);

  useEffect(() => {
    if (!selectedProjectId) return;

    loadPvData(selectedProjectId);
  }, [
    currentRevision,
    getValues,
    reset,
    selectedModelingRevision,
    selectedProjectId,
    setValue,
  ]);

  return (
    <PVContext.Provider
      value={{
        projectId: activeProjectId,
        selectedProjectId,
        setSelectedProjectId,
        control,
        setValue,
        getValues,
        currentRevision,
        revisionLoadVersion,
        savePVData,
        loadPvData,
      }}
    >
      {children}
    </PVContext.Provider>
  );
}

export { PVProvider, PVContext };